import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@app/i18n';
import Tooltip from '@mui/material/Tooltip';
import { Button } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import s from './RefreshButton.module.css';

/**
 * Botão **Atualizar** de T15 (pedido do mrolim, 04/09) — quatro estados:
 *
 * | estado       | rótulo             | aparência                                      |
 * |---|---|---|
 * | `idle`       | Atualizar          | contorno normal, ícone parado                  |
 * | `refreshing` | Atualizando…       | fundo verde suave, ícone girando, desabilitado |
 * | `done`       | Atualizado!        | fundo verde suave, pulso de ~10 %, ícone parado |
 * | `error`      | Falha ao atualizar | cor de erro, tooltip com o motivo              |
 *
 * De `done` volta a `idle` quando o mouse passa por cima (ou o foco chega) de
 * novo; de `error` sai só com um novo clique. O clique **força a leitura viva**
 * — quem o recebe é `onRefresh`, que rejeita com o motivo quando ela falha.
 *
 * `prefers-reduced-motion`: sem giro e sem pulso (o `global.css` já zera toda
 * animação; o módulo repete a regra para o botão não depender disso).
 */

export type RefreshState = 'idle' | 'refreshing' | 'done' | 'error';

export interface RefreshButtonProps {
  /** Dispara a leitura viva. Rejeita quando ela falha; o erro vira o tooltip. */
  onRefresh: () => Promise<unknown>;
  /** Texto do tooltip no estado de erro. Padrão: a mensagem do erro. */
  errorReason?: (error: unknown) => string;
  className?: string;
}

/** Chave de catálogo por estado — o texto sai no render (`t`), não aqui. */
const REFRESH_KEY: Record<RefreshState, string> = {
  idle: 'runtimeStatus.refresh.idle',
  refreshing: 'runtimeStatus.refresh.refreshing',
  done: 'runtimeStatus.refresh.done',
  error: 'runtimeStatus.refresh.error',
};

function describe(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return t('runtimeStatus.page.refreshFailed');
}

export function RefreshButton({ onRefresh, errorReason, className }: RefreshButtonProps) {
  const [state, setState] = useState<RefreshState>('idle');
  const [reason, setReason] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (state === 'refreshing') return;
    setState('refreshing');
    setReason(null);
    try {
      await onRefresh();
      if (mounted.current) setState('done');
    } catch (error) {
      if (!mounted.current) return;
      setReason(errorReason ? errorReason(error) : describe(error));
      setState('error');
    }
  }, [state, onRefresh, errorReason]);

  // «Atualizado!» fica até a pessoa voltar ao botão; aí ele é «Atualizar» de novo.
  const rest = useCallback(() => {
    if (state === 'done') setState('idle');
  }, [state]);

  const button = (
    <Button
      variant="out"
      className={[s.btn, s[state], className].filter(Boolean).join(' ')}
      data-state={state}
      disabled={state === 'refreshing'}
      aria-busy={state === 'refreshing' || undefined}
      onClick={() => void refresh()}
      onMouseEnter={rest}
      onFocus={rest}
    >
      <Icon name="refresh" className={state === 'refreshing' ? s.spin : undefined} />
      <span aria-live="polite">{t(REFRESH_KEY[state])}</span>
    </Button>
  );

  if (state !== 'error' || !reason) return button;
  return (
    <Tooltip title={reason} arrow describeChild>
      {button}
    </Tooltip>
  );
}
